import { useEffect, useState } from 'react'

import { numberToDecimalString } from 'commons/utils/number'
import {
  Card,
  Icon,
  LoadingModal,
  NotFound,
  Page,
  SlimButton,
  StatisticsCard,
  TabsList,
} from 'components'
import { CentersFactories } from 'constants/enums'
import { FormProvider, useForm } from 'react-hook-form'
import {
  asyncGetExtraVolumeData,
  asyncGetRejectMotiveList,
  setMaterialList,
} from 'store/ExtraVolumeContext/ExtraVolumeSlice'
import type { TipoCriticaVolume } from 'store/ExtraVolumeContext/Types/ExtraVolumeType'
import { useDispatch, useSelector } from 'store/store'

import { Filter } from './components/Filter'
import UploadAnalysisErrorModal from './components/UploadAnalysisErrorModal'
import VolumeTable from './components/VolumeTable'
import * as S from './styles'

export const ExtraVolumePage = () => {
  const dispatch = useDispatch()
  const [hasFilter, setHasFilter] = useState(false)

  const { materialsList, loadingExtraVolume, totalizer, rejectionMotives, uploadErrors } =
    useSelector((store) => store.extraVolume)

  const defaultValues = {
    CodigoCentroMaterial: CentersFactories.A022 as string,
    EquipeVenda: [], // Equipes
    EscritorioVenda: [],
    Materias: [], // TODO: check if this is correct
    Mercado: [],
    MesesCota: [],
    numeroPagina: 0,
    RegiaoVenda: [], // Zona (Regioes)
    registrosPaginas: 20,
    TipoCritica: [] as TipoCriticaVolume[],
  }

  const methods = useForm({
    defaultValues,
  })

  const getData = (obj: typeof defaultValues, totalizer = true) => {
    const { TipoCritica, Materias, Mercado, EscritorioVenda, EquipeVenda, RegiaoVenda, MesesCota } =
      obj
    const emptyFilters = [
      TipoCritica,
      Materias,
      Mercado,
      EscritorioVenda,
      EquipeVenda,
      RegiaoVenda,
      MesesCota,
    ].every((i) => i[0] === '')
    setHasFilter(!emptyFilters)

    dispatch(asyncGetExtraVolumeData(obj, totalizer))
  }

  const handlePageChange = (page: number) => {
    getData(
      {
        ...methods.getValues(),
        numeroPagina: page - 1,
      },
      false,
    )
  }

  useEffect(() => {
    getData(methods.getValues())
    if (!rejectionMotives?.length) {
      dispatch(asyncGetRejectMotiveList())
    }
  }, [])

  const onFilterSubmit = () => {
    getData(methods.getValues())
  }

  const onFilterClear = () => {
    methods.reset(defaultValues)
    getData(defaultValues)
  }

  const handleFactoryChange = (name: string) => {
    dispatch(setMaterialList([]))
    const newFilter = { ...methods.getValues(), CodigoCentroMaterial: name }
    const factory = totalizer.TotalizadorPorCentro.find((t) => t.CodigoCentroMaterial === name)
    if (factory?.QuantidadePares) {
      getData(newFilter, false)
    }
    methods.setValue('CodigoCentroMaterial', name)
  }

  const notFoundContent = () => (
    <S.NotFoundWrapper>
      {!hasFilter ? (
        <NotFound
          description={
            <S.NotFoundContentWrapper>Verifique novamente mais tarde</S.NotFoundContentWrapper>
          }
          icon={<Icon icon="empty" size="50" />}
          title={`Nenhuma análise pendente nesta fábrica`}
        />
      ) : (
        <NotFound
          description={
            <S.NotFoundContentWrapper>
              Verifique os critérios utilizados e tente novamente.
              <SlimButton
                content="Reiniciar busca e filtros"
                icon={<Icon icon="chevron-left" size="15" />}
                onClick={onFilterClear}
                type="button"
                variant="white"
              />{' '}
            </S.NotFoundContentWrapper>
          }
          title={`Nenhum resultado encontrado em linha nesta fábrica`}
        />
      )}
    </S.NotFoundWrapper>
  )

  const renderTable = () =>
    materialsList.length ? (
      <VolumeTable
        handlePageChange={handlePageChange}
        payload={materialsList}
        updateTable={() => getData(methods.getValues())}
      />
    ) : (
      notFoundContent()
    )

  return (
    <FormProvider {...methods}>
      <Page fullwidth>
        {loadingExtraVolume ? (
          <LoadingModal />
        ) : (
          <>
            <Filter
              disableFilters={!hasFilter && !materialsList.length}
              onClear={onFilterClear}
              onSubmit={onFilterSubmit}
            />
            <S.WrapStatistics>
              <StatisticsCard
                icon={<Icon color="#94A3BC" icon="clock" size="25" />}
                mainValue={numberToDecimalString(totalizer.TotalQuantidadePares)}
                title="Volume p/ analisar"
              >
                <S.WrapTotalizers>
                  <S.TotalizerContent>
                    <b>Crítica por cota</b>
                    {numberToDecimalString(totalizer.TotalCriticaPorCota)}
                  </S.TotalizerContent>
                  <S.TotalizerContent>
                    <b>Crítica por estoque</b>
                    {numberToDecimalString(totalizer.TotalCriticaPorEstoque)}
                  </S.TotalizerContent>
                  <S.TotalizerContent>
                    <b>Crítica por cota e estoque</b>
                    {numberToDecimalString(totalizer.TotalCriticaPorCotaEstoque)}
                  </S.TotalizerContent>
                </S.WrapTotalizers>
              </StatisticsCard>
            </S.WrapStatistics>
            {totalizer?.TotalizadorPorCentro.length ? (
              <TabsList
                initialTab={totalizer.TotalizadorPorCentro.findIndex(
                  (fab) => fab.CodigoCentroMaterial === methods.getValues('CodigoCentroMaterial'),
                )}
                onClick={handleFactoryChange}
                showTotalizer
              >
                {totalizer?.TotalizadorPorCentro.map((center) => (
                  <TabsList.Tab
                    disabled={!center.QuantidadePares}
                    key={center.CodigoCentroMaterial}
                    label={center.CodigoCentroMaterial}
                    tooltip={
                      !center.QuantidadePares ? (
                        <p>
                          No momento não existem solicitações
                          <br /> pendentes nesta fábrica
                        </p>
                      ) : (
                        <></>
                      )
                    }
                    totalizer={center.QuantidadePares}
                  >
                    {renderTable()}
                  </TabsList.Tab>
                ))}
              </TabsList>
            ) : (
              <Card>{renderTable()}</Card>
            )}
          </>
        )}
        <UploadAnalysisErrorModal data={uploadErrors} />
      </Page>
    </FormProvider>
  )
}

export default ExtraVolumePage
